/**
 * API клієнти Блоку 6 (Договори/Розрахунки) — Фаза 2.1 #4.
 * 3 ресурси у одному файлі: reconciliation_acts, powers_of_attorney,
 * settlement_inventories.
 */
import { api } from './client';
import type { Page } from './catalogs';

// ===== ReconciliationAct =====

export type ReconciliationState = 'DRAFT' | 'SENT' | 'AGREED' | 'DISPUTED';

export interface ReconciliationAct {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  external_source?: string | null;
  comment: string | null;
  contractor_id: string;
  contract_id: string | null;
  period_start: string;
  period_end: string;
  opening_balance_our: string;
  closing_balance_our: string;
  opening_balance_their: string | null;
  closing_balance_their: string | null;
  discrepancy_amount: string;
  state: ReconciliationState;
  sent_to_contractor_at: string | null;
  agreed_at: string | null;
  movements_snapshot: unknown;
  created_at: string;
  updated_at: string;
}

export interface ReconciliationActCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  comment?: string | null;
  contractor_id: string;
  contract_id?: string | null;
  period_start: string;
  period_end: string;
  opening_balance_our?: string;
  closing_balance_our?: string;
}

export interface ReconciliationActFromRegisters {
  organization_id: string;
  contractor_id: string;
  contract_id?: string | null;
  period_start: string;
  period_end: string;
  document_date: string;
  comment?: string | null;
}

export const reconciliationActs = {
  list: (params?: {
    organization_id?: string;
    contractor_id?: string;
    state?: ReconciliationState;
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<ReconciliationAct>>('/documents/reconciliation-acts', { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<ReconciliationAct>(`/documents/reconciliation-acts/${id}`).then((r) => r.data),
  create: (payload: ReconciliationActCreate) =>
    api.post<ReconciliationAct>('/documents/reconciliation-acts', payload).then((r) => r.data),
  fromRegisters: (payload: ReconciliationActFromRegisters) =>
    api.post<ReconciliationAct>('/documents/reconciliation-acts/from-registers', payload)
      .then((r) => r.data),
  update: (id: string, payload: { comment?: string | null; period_start?: string; period_end?: string }) =>
    api.patch<ReconciliationAct>(`/documents/reconciliation-acts/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/documents/reconciliation-acts/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<ReconciliationAct>(`/documents/reconciliation-acts/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<ReconciliationAct>(`/documents/reconciliation-acts/${id}/unpost`).then((r) => r.data),
  sendToContractor: (id: string) =>
    api.post<ReconciliationAct>(`/documents/reconciliation-acts/${id}/send-to-contractor`).then((r) => r.data),
  confirmFromContractor: (id: string, payload: { opening_balance_their: string; closing_balance_their: string }) =>
    api.post<ReconciliationAct>(`/documents/reconciliation-acts/${id}/confirm-from-contractor`, payload).then((r) => r.data),
  markAgreed: (id: string) =>
    api.post<ReconciliationAct>(`/documents/reconciliation-acts/${id}/mark-agreed`).then((r) => r.data),
  markDisputed: (id: string) =>
    api.post<ReconciliationAct>(`/documents/reconciliation-acts/${id}/mark-disputed`).then((r) => r.data),
};


// ===== PowerOfAttorney =====

export type PowerOfAttorneyState = 'ACTIVE' | 'USED' | 'EXPIRED' | 'REVOKED';

export interface PowerOfAttorney {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  external_source?: string | null;
  comment: string | null;
  employee_id: string;
  contractor_id: string;
  contract_id: string | null;
  authorized_actions: string;
  valid_from: string;
  valid_until: string;
  state: PowerOfAttorneyState;
  used_at: string | null;
  used_in_doc_type: string | null;
  used_in_doc_id: string | null;
  revoked_at: string | null;
  revocation_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface PowerOfAttorneyCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  comment?: string | null;
  employee_id: string;
  contractor_id: string;
  contract_id?: string | null;
  authorized_actions: string;
  valid_from: string;
  valid_until: string;
}

export const powersOfAttorney = {
  list: (params?: { organization_id?: string; state?: PowerOfAttorneyState; limit?: number; offset?: number }) =>
    api.get<Page<PowerOfAttorney>>('/documents/powers-of-attorney', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<PowerOfAttorney>(`/documents/powers-of-attorney/${id}`).then((r) => r.data),
  create: (payload: PowerOfAttorneyCreate) =>
    api.post<PowerOfAttorney>('/documents/powers-of-attorney', payload).then((r) => r.data),
  update: (id: string, payload: Partial<Omit<PowerOfAttorneyCreate, 'organization_id' | 'employee_id' | 'contractor_id'>>) =>
    api.patch<PowerOfAttorney>(`/documents/powers-of-attorney/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/documents/powers-of-attorney/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<PowerOfAttorney>(`/documents/powers-of-attorney/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<PowerOfAttorney>(`/documents/powers-of-attorney/${id}/unpost`).then((r) => r.data),
  revoke: (id: string, revocation_reason: string) =>
    api.post<PowerOfAttorney>(`/documents/powers-of-attorney/${id}/revoke`, { revocation_reason })
      .then((r) => r.data),
};


// ===== SettlementInventory =====

export type SettlementInventoryState = 'IN_PROGRESS' | 'COMPLETED';

export interface SettlementInventoryItem {
  id: string;
  line_number: number;
  contractor_id: string;
  contract_id: string | null;
  book_amount: string;
  confirmed_amount: string | null;
  variance_amount: string;
  variance_reason: string | null;
}

export interface SettlementInventory {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  external_source?: string | null;
  comment: string | null;
  inventory_date: string;
  state: SettlementInventoryState;
  commission_chairperson_id: string | null;
  total_book_amount: string;
  total_confirmed_amount: string;
  total_variance_amount: string;
  items: SettlementInventoryItem[];
  created_at: string;
  updated_at: string;
}

export interface SettlementInventoryCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  comment?: string | null;
  inventory_date: string;
  commission_chairperson_id?: string | null;
  items?: Array<{
    line_number: number;
    contractor_id: string;
    contract_id?: string | null;
    book_amount: string;
    confirmed_amount?: string | null;
    variance_reason?: string | null;
  }>;
}

export interface SettlementInventoryFromRegisters {
  organization_id: string;
  inventory_date: string;
  document_date: string;
  commission_chairperson_id?: string | null;
  comment?: string | null;
}

export const settlementInventories = {
  list: (params?: { organization_id?: string; state?: SettlementInventoryState; limit?: number; offset?: number }) =>
    api.get<Page<SettlementInventory>>('/documents/settlement-inventories', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<SettlementInventory>(`/documents/settlement-inventories/${id}`).then((r) => r.data),
  create: (payload: SettlementInventoryCreate) =>
    api.post<SettlementInventory>('/documents/settlement-inventories', payload).then((r) => r.data),
  fromRegisters: (payload: SettlementInventoryFromRegisters) =>
    api.post<SettlementInventory>('/documents/settlement-inventories/from-registers', payload).then((r) => r.data),
  update: (id: string, payload: { comment?: string | null; inventory_date?: string }) =>
    api.patch<SettlementInventory>(`/documents/settlement-inventories/${id}`, payload).then((r) => r.data),
  updateItem: (docId: string, itemId: string, payload: { confirmed_amount?: string | null; variance_reason?: string | null }) =>
    api.patch<SettlementInventory>(`/documents/settlement-inventories/${docId}/items/${itemId}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/documents/settlement-inventories/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<SettlementInventory>(`/documents/settlement-inventories/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<SettlementInventory>(`/documents/settlement-inventories/${id}/unpost`).then((r) => r.data),
};
